import type { SubjectCanvasNode, SubjectCollection } from '@/types'

/**
 * Decide whether a multi-select of subjects is eligible to be grouped into a
 * subject collection through the "Group into Collection" UI affordance, and if
 * so what mode the action should run in.
 *
 * Eligibility rules:
 * - 2+ subjects selected
 * - all selected subjects exist and live in the same scene
 * - no selected subject is in a variation stack (stack/collection mutual
 *   exclusion)
 * - selected subjects do not span two or more distinct existing collections
 *   (ambiguous intent; the user should pick a target explicitly)
 * - if every selected subject is already a member of the same collection
 *   there's nothing to do, so the action is hidden
 *
 * When exactly one existing collection is represented and some selected
 * subjects are unaffiliated, the action becomes "add the unaffiliated members
 * to that collection" rather than creating a new collection. Otherwise it
 * creates a new collection from the selection in selection order.
 */
export type SubjectGroupingEligibility =
  | { canGroup: false }
  | { canGroup: true; mode: 'create'; subjectIds: string[]; sceneId: string }
  | {
      canGroup: true
      mode: 'addToExisting'
      subjectIds: string[]
      sceneId: string
      targetCollectionId: string
      targetCollectionName: string
    }

export function evaluateSubjectGroupingEligibility(
  selectedSubjectIds: Iterable<string>,
  subjects: Pick<Map<string, SubjectCanvasNode>, 'get'>,
  collections: Pick<Map<string, SubjectCollection>, 'get'>,
): SubjectGroupingEligibility {
  const subjectIds: string[] = []
  let sceneId: string | undefined
  const representedCollectionIds = new Set<string>()

  for (const id of selectedSubjectIds) {
    const subject = subjects.get(id)
    if (!subject) return { canGroup: false }
    if (subject.stackId) return { canGroup: false }
    if (sceneId === undefined) sceneId = subject.sceneId
    else if (subject.sceneId !== sceneId) return { canGroup: false }
    if (subject.collectionId) representedCollectionIds.add(subject.collectionId)
    subjectIds.push(id)
  }

  if (!sceneId || subjectIds.length < 2) return { canGroup: false }
  if (representedCollectionIds.size > 1) return { canGroup: false }

  if (representedCollectionIds.size === 1) {
    const targetCollectionId = Array.from(representedCollectionIds)[0]
    const unaffiliated = subjectIds.filter(id => !subjects.get(id)?.collectionId)
    if (unaffiliated.length === 0) return { canGroup: false }
    const targetCollection = collections.get(targetCollectionId)
    if (!targetCollection) return { canGroup: false }
    return {
      canGroup: true,
      mode: 'addToExisting',
      subjectIds: unaffiliated,
      sceneId,
      targetCollectionId,
      targetCollectionName: targetCollection.name,
    }
  }

  return { canGroup: true, mode: 'create', subjectIds, sceneId }
}

/**
 * Default name to use when the user creates a subject collection without
 * supplying one explicitly.
 *
 * The collection name is the *noun*, not a description of the act of
 * collecting. So `Cute Hat` + `Scary Hat` becomes `Hat`, not `Hat variants`
 * or `Hat Collection`. When there's no shared trailing token we fall back
 * to the first subject's name as-is (it can be renamed inline). When the
 * list is empty we use a generic fallback. The word "variant" is internal
 * jargon and does not appear in user-facing copy.
 *
 * Token comparison is case-insensitive but preserves the casing of the
 * first subject's tokens so a typed name like `iPhone` doesn't become
 * `iphone`.
 */
export function deriveDefaultCollectionName(memberNames: ReadonlyArray<string>, fallback = 'Untitled'): string {
  const cleaned = memberNames.map(name => name.trim()).filter(name => name.length > 0)
  if (cleaned.length === 0) return fallback
  if (cleaned.length === 1) return cleaned[0]

  const tokenLists = cleaned.map(name => name.split(/\s+/))
  const sharedSuffix: string[] = []
  const minLen = Math.min(...tokenLists.map(tokens => tokens.length))
  for (let offset = 1; offset <= minLen; offset += 1) {
    const tokensAtOffset = tokenLists.map(tokens => tokens[tokens.length - offset])
    const lowerRef = tokensAtOffset[0].toLowerCase()
    if (tokensAtOffset.every(token => token.toLowerCase() === lowerRef)) {
      sharedSuffix.unshift(tokensAtOffset[0])
    } else {
      break
    }
  }

  if (sharedSuffix.length > 0 && sharedSuffix.length < tokenLists[0].length) {
    return sharedSuffix.join(' ')
  }
  // No shared trailing tokens, or every member's name is identical
  // (which would already read as the same identity). Use the first
  // subject as a starting point — the user can rename inline.
  return cleaned[0]
}
